import * as React from 'react';
// eslint-disable-next-line no-restricted-syntax -- this IS the design-system Text; it wraps the RN primitive
import { Text as RNText } from 'react-native';
import { Text as TextSlot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { resolveWeightClasses, resolveWeightStyle } from './text-weight';

const textVariants = cva('font-sans text-base text-foreground', {
  variants: {
    variant: {
      default: '',
      h1: 'text-4xl font-sans-bold tracking-tight',
      h2: 'text-3xl font-sans-bold tracking-tight',
      h3: 'text-2xl font-sans-semibold',
      h4: 'text-xl font-sans-semibold',
      p: 'text-base leading-6',
      lead: 'text-xl text-muted-foreground',
      large: 'text-lg font-sans-semibold',
      small: 'text-sm font-sans-medium leading-5',
      muted: 'text-sm text-muted-foreground leading-5',
      code: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm',
      blockquote: 'border-l-2 border-border pl-6 italic',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type TextProps = React.ComponentPropsWithoutRef<typeof RNText> &
  VariantProps<typeof textVariants> & {
    asChild?: boolean;
  };

const Text = React.forwardRef<React.ElementRef<typeof RNText>, TextProps>(
  ({ className, variant, asChild = false, style, ...props }, ref) => {
    const Component = asChild ? TextSlot : RNText;

    // Rewrite any caller-supplied weight utilities (font-semibold, etc.) to real
    // Hanken weight families, then re-merge so a single family token wins.
    const resolvedClassName = cn(
      resolveWeightClasses(cn(textVariants({ variant, className })))
    );

    return (
      <Component
        ref={ref}
        className={resolvedClassName}
        style={resolveWeightStyle(style)}
        {...props}
      />
    );
  }
);

Text.displayName = 'Text';

export { Text, textVariants };
export type { TextProps };
